import { detectImageMimeType } from './detectImageMimeType';

const EXTENSION_ALIASES: Readonly<Record<string, readonly string[]>> = { jpg: ['jpg', 'jpeg'] };

function ensureExtension(name: string, ext: string): string {
	const lower = name.toLowerCase();
	const accepted = EXTENSION_ALIASES[ext] ?? [ext];
	return accepted.some((alias) => lower.endsWith(`.${alias}`)) ? name : `${name}.${ext}`;
}

/**
 * Reads the leading bytes of a blob to detect its image type, returning `null` when the bytes aren't a
 * recognized image or when the blob cannot be read (e.g. `Blob.arrayBuffer` is unavailable on the
 * target browser). Isolated so a read failure falls back to the untyped file rather than propagating.
 */
async function sniffImageType(blob: Blob): Promise<{ mime: string; ext: string } | null> {
	try {
		return detectImageMimeType(new Uint8Array(await blob.slice(0, 16).arrayBuffer()));
	} catch (e) {
		return null;
	}
}

/**
 * Builds an image `File` from a blob, guaranteeing a valid `image/*` MIME type and a matching
 * filename extension. When the blob's own type isn't `image/*` (some origins serve images with an
 * empty or `binary/octet-stream` Content-Type), the type and extension are recovered by sniffing the
 * leading magic bytes. Blobs whose bytes aren't a recognized image keep their original type and name.
 */
async function toTypedImageFile(blob: Blob, derivedFilename: string): Promise<File> {
	if (/^image\//.test(blob.type)) {
		return new File([blob], derivedFilename, { type: blob.type });
	}

	const detected = await sniffImageType(blob);
	if (!detected) {
		return new File([blob], derivedFilename, { type: blob.type });
	}

	return new File([blob], ensureExtension(derivedFilename, detected.ext), { type: detected.mime });
}

export async function getFile(url: string, filename?: string, includeType?: boolean): Promise<File | null> {
	try {
		const parsedUrl = new URL(url, 'https://dummy.com'); // we just need a base URL to retrieve the pathname
		const result = await fetch(url);
		const blob = await result.blob();

		const derivedFilename =
			filename || decodeURIComponent(parsedUrl.pathname.split('/').pop() || '').split('?')[0] || 'file';

		if (includeType) {
			return await toTypedImageFile(blob, derivedFilename);
		}

		return new File([blob], derivedFilename);
	} catch (e) {
		return null;
	}
}
